/**
 * The desktop Explorer (app-clarity "IDE sidebar", docs/ux/design-language.md §7c–7d): replaces
 * the project tab strip at ≥1280 px. Home, then each pinned project (monogram tile, 15 px
 * semibold, fixed count columns ⬣ blocked · ◆ needs you · running), then its lanes and their live
 * runs, with VS Code chevrons (16 px column, 120 ms rotation) and indent guides.
 *
 * Keys: ↑/↓ move, ←/→ collapse and expand (→ on an open row steps in, ← steps out), ⏎ opens, `/`
 * filters (while the tree has focus), ⌥⇧↑/↓ reorders a project, ⇧F10 opens its menu, ⌘B hides
 * the sidebar. ⌥0–9 / ⌘0–9 still switch to Home and the first nine projects in tree order.
 * Resizable 220–420 px (drag the grip). Selection is focus-aware: accent while the tree has
 * keyboard focus, neutral otherwise.
 */
import { Link, useNavigate } from '@tanstack/react-router'
import { Button, Sheet, StatusIcon } from '@viko/transcript-ui'
import { ChevronRight, Clock3, House, MoreHorizontal, Plus } from 'lucide-react'
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  clampWidth,
  type ExplorerRow,
  explorerRows,
  lanesOf,
  loadOpen,
  projectNodeId,
  SIDEBAR_MAX,
  SIDEBAR_MIN,
  saveOpen,
  saveWidth,
} from '../../lib/explorer.js'
import { projectHref, runHref, workstreamHref } from '../../lib/ia.js'
import { machineLine } from '../../lib/machine-line.js'
import { useMachines, useRuns, useWorkstreams } from '../../lib/queries.js'
import { tabKeyShortcuts, tabShortcut } from '../../lib/shortcut.js'
import { placeOf } from '../../lib/tab-memory.js'
import { TAB_KEYS } from '../../lib/tab-order.js'
import { useInboxRows } from '../inbox/useInboxRows.js'
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import type { TabItem } from '../tabs/ProjectTabs.js'
import '../lanes/lanes.css'
import './explorer.css'

export interface ExplorerProps {
  tabs: TabItem[]
  unpinned: { id: string; name: string }[]
  current?: string | undefined
  standalone: boolean
  totals: { needs: number; running: number }
  width: number
  onWidth: (w: number) => void
  onMove: (id: string, to: number) => void
  onUnpin: (id: string) => void
  onPin: (id: string) => void
  onArchive: (id: string) => void
  onAdd: () => void
  /** ⌘B: hide the sidebar (the header's keycap button). */
  onHide?: () => void
  /** `page`: the phone's "Projects and lanes" page (full width, no grip, no machines footer). */
  variant?: 'side' | 'page'
}

export function Explorer(p: ExplorerProps) {
  const needs = useNeedsYou()
  const built = useInboxRows()
  const runs = useRuns()
  const workstreams = useWorkstreams()
  const nav = useNavigate()
  const [open, setOpen] = useState<Set<string>>(() => {
    const s = loadOpen()
    if (p.current && p.current !== 'home' && p.current !== 'settings')
      s.add(projectNodeId(p.current))
    return s
  })
  const [filter, setFilter] = useState('')
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null)
  const [overflow, setOverflow] = useState<{ x: number; y: number } | null>(null)
  const [archive, setArchive] = useState<TabItem | null>(null)
  const tree = useRef<HTMLDivElement>(null)
  const filterInput = useRef<HTMLInputElement>(null)
  const page = p.variant === 'page'
  const shown = p.tabs.slice(0, TAB_KEYS)
  const extra = p.tabs.slice(TAB_KEYS)

  // The current project opens in the tree when you switch to it.
  useEffect(() => {
    if (!p.current || p.current === 'home' || p.current === 'settings') return
    const id = projectNodeId(p.current)
    setOpen((s) => (s.has(id) ? s : new Set([...s, id])))
  }, [p.current])

  const laneNeeds = useMemo(() => {
    const m = new Map<string, number>()
    for (const r of built.rows)
      if (r.blocking && r.workstreamId) m.set(r.workstreamId, (m.get(r.workstreamId) ?? 0) + 1)
    return m
  }, [built.rows])

  const rows = useMemo(
    () =>
      explorerRows({
        projects: shown.map((t) => ({ id: t.id, name: t.name, running: t.running })),
        totals: {
          blocked: needs.blocked,
          needs: needs.decide,
          running: p.totals.running,
        },
        counts: (id) => ({ blocked: needs.blockedOf(id), needs: needs.decideOf(id) }),
        laneNeeds,
        lanes: lanesOf(workstreams.data ?? []),
        runs: runs.data ?? [],
        open,
        filter,
      }),
    [shown, needs, p.totals.running, laneNeeds, workstreams.data, runs.data, open, filter],
  )

  const toggle = (id: string, to?: boolean) =>
    setOpen((s) => {
      const next = new Set(s)
      if (to ?? !next.has(id)) next.add(id)
      else next.delete(id)
      saveOpen(next)
      return next
    })

  const hrefOf = (r: ExplorerRow): string => {
    if (r.kind === 'home') return placeOf('home', '/home', p.current)
    if (r.kind === 'project' && r.projectId)
      return placeOf(r.projectId, projectHref(r.projectId), p.current)
    if (r.kind === 'lane' && r.projectId) return workstreamHref(r.id.slice(2), r.projectId)
    if (r.kind === 'run' && r.runId) return runHref(r.runId, r.projectId)
    return '/home'
  }
  const isCurrent = (r: ExplorerRow) =>
    (r.kind === 'home' && p.current === 'home') ||
    (r.kind === 'project' && r.projectId === p.current)

  const focusRow = (id: string | undefined) =>
    id &&
    requestAnimationFrame(() =>
      tree.current?.querySelector<HTMLElement>(`[data-node="${CSS.escape(id)}"] .xp-link`)?.focus(),
    )

  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-node]')
    const id = el?.dataset.node
    const i = rows.findIndex((r) => r.id === id)
    const row = rows[i]
    if (!row) return
    if (e.altKey && e.shiftKey && row.kind === 'project' && row.projectId) {
      const dir =
        e.key === 'ArrowUp' || e.key === 'ArrowLeft'
          ? -1
          : e.key === 'ArrowDown' || e.key === 'ArrowRight'
            ? 1
            : 0
      if (!dir) return
      e.preventDefault()
      const at = p.tabs.findIndex((t) => t.id === row.projectId)
      const to = at + dir
      if (to >= 0 && to < p.tabs.length) {
        p.onMove(row.projectId, to)
        focusRow(row.id)
      }
      return
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      focusRow(rows[i + (e.key === 'ArrowDown' ? 1 : -1)]?.id)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      if (row.expandable && !row.open) toggle(row.id, true)
      else if (row.open) focusRow(rows[i + 1]?.id)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (row.open) toggle(row.id, false)
      else if (row.parent) focusRow(row.parent)
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      focusRow((e.key === 'Home' ? rows[0] : rows.at(-1))?.id)
    } else if (e.key === '/' && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      filterInput.current?.focus()
    } else if (
      (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) &&
      row.kind === 'project' &&
      row.projectId
    ) {
      e.preventDefault()
      openMenu(row.projectId, e.target as HTMLElement)
    }
  }

  const host = useRef<HTMLElement>(null)
  const openMenu = (projectId: string, target: HTMLElement, ev?: MouseEvent) => {
    const h = host.current?.getBoundingClientRect()
    const r = target.getBoundingClientRect()
    setMenu({
      id: projectId,
      x: (ev?.clientX ?? r.left + 24) - (h?.left ?? 0),
      y: (ev ? ev.clientY : r.bottom) - (h?.top ?? 0) + 4,
    })
  }

  // Resize: drag the grip (220–420 px); the width is remembered per browser.
  const drag = useRef<{ x: number; w: number } | null>(null)
  const onGripDown = (e: PointerEvent<HTMLElement>) => {
    drag.current = { x: e.clientX, w: p.width }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onGripMove = (e: PointerEvent<HTMLElement>) => {
    if (!drag.current) return
    p.onWidth(clampWidth(drag.current.w + e.clientX - drag.current.x))
  }
  const onGripUp = () => {
    if (!drag.current) return
    drag.current = null
    saveWidth(p.width)
  }

  const menuTab = menu ? p.tabs.findIndex((t) => t.id === menu.id) : -1
  return (
    <aside
      className={`xp${page ? ' is-page' : ''}`}
      ref={host}
      aria-label={page ? 'Projects and lanes' : 'Explorer'}
      style={page ? undefined : { width: p.width }}
    >
      <div className="xp-head">
        {page ? (
          <h1 className="xp-title">Projects and lanes</h1>
        ) : (
          <span className="xp-brand">Explorer</span>
        )}
        <span className="xp-grow" />
        <button
          type="button"
          className="xp-tool"
          aria-label="Add a project"
          title="Add a project"
          onClick={p.onAdd}
        >
          <Plus size={16} aria-hidden="true" />
        </button>
        {extra.length || p.unpinned.length ? (
          <button
            type="button"
            className="xp-tool"
            aria-label={`More projects (${extra.length + p.unpinned.length})`}
            aria-expanded={!!overflow}
            aria-haspopup="menu"
            onClick={(e) => {
              const h = host.current?.getBoundingClientRect()
              const r = e.currentTarget.getBoundingClientRect()
              setMenu(null)
              setOverflow((o) =>
                o ? null : { x: r.left - (h?.left ?? 0) - 160, y: r.bottom - (h?.top ?? 0) + 4 },
              )
            }}
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
        ) : null}
        {p.onHide ? (
          <button
            type="button"
            className="xp-hide"
            aria-label="Hide the Explorer"
            aria-keyshortcuts="Meta+B Control+B"
            title="Hide the Explorer (⌘B)"
            onClick={p.onHide}
          >
            <kbd>⌘B</kbd>
          </button>
        ) : null}
      </div>
      <div className="xp-filter">
        <label className="sr-only" htmlFor={page ? 'xp-filter-page' : 'xp-filter'}>
          Filter projects and work
        </label>
        <input
          id={page ? 'xp-filter-page' : 'xp-filter'}
          ref={filterInput}
          value={filter}
          placeholder="Filter"
          autoComplete="off"
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              setFilter('')
              focusRow(rows[0]?.id)
            } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
              e.preventDefault()
              focusRow(rows[0]?.id)
            }
          }}
        />
        {filter ? null : (
          <span className="xp-filter__key" aria-hidden="true">
            <kbd>/</kbd>
          </span>
        )}
      </div>
      <nav aria-label="Main" className="xp-nav">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: delegates arrow keys from the focusable row links inside */}
        <div className="xp-tree" ref={tree} onKeyDown={onKey}>
          <ul className="xp-list">
            {rows.map((r, i) => (
              <Node
                key={r.id}
                row={r}
                first={i === 0}
                prevKind={rows[i - 1]?.kind}
                href={hrefOf(r)}
                current={isCurrent(r)}
                standalone={p.standalone}
                onToggle={() => toggle(r.id)}
                onContext={(e) => {
                  if (r.kind !== 'project' || !r.projectId) return
                  e.preventDefault()
                  openMenu(r.projectId, e.currentTarget as HTMLElement, e)
                }}
              />
            ))}
          </ul>
          {filter && !rows.length ? <p className="xp-empty">Nothing matches “{filter}”.</p> : null}
        </div>
      </nav>
      {page ? null : (
        <div className="xp-foot">
          <Machines />
          <p className="xp-keys" aria-hidden="true">
            ↑↓ move · ←→ fold · ⏎ open
          </p>
        </div>
      )}
      {/* The window-splitter pattern: drag, or focus it and use ←/→. */}
      {page ? null : (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the Explorer"
          aria-valuemin={SIDEBAR_MIN}
          aria-valuemax={SIDEBAR_MAX}
          aria-valuenow={p.width}
          tabIndex={0}
          className="xp-grip"
          title="Drag to resize"
          onPointerDown={onGripDown}
          onPointerMove={onGripMove}
          onPointerUp={onGripUp}
          onPointerCancel={onGripUp}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            e.preventDefault()
            const w = clampWidth(p.width + (e.key === 'ArrowRight' ? 16 : -16))
            p.onWidth(w)
            saveWidth(w)
          }}
        />
      )}
      {menu ? (
        <XpMenu
          at={menu}
          onClose={() => setMenu(null)}
          items={[
            ...(menuTab > 0
              ? [{ label: 'Move up', run: () => p.onMove(menu.id, menuTab - 1) }]
              : []),
            ...(menuTab >= 0 && menuTab < p.tabs.length - 1
              ? [{ label: 'Move down', run: () => p.onMove(menu.id, menuTab + 1) }]
              : []),
            { label: 'Unpin', run: () => p.onUnpin(menu.id), sep: true },
            {
              label: 'Project settings',
              run: () => void nav({ href: projectHref(menu.id, 'settings') }),
            },
            {
              label: 'Archive…',
              run: () => setArchive(p.tabs.find((t) => t.id === menu.id) ?? null),
            },
          ]}
        />
      ) : null}
      {overflow ? (
        <XpMenu
          at={overflow}
          onClose={() => setOverflow(null)}
          label="More projects"
          items={[
            ...extra.map((t) => ({
              label: t.name,
              run: () => void nav({ href: projectHref(t.id) }),
            })),
            ...p.unpinned.map((x) => ({ label: `Pin ${x.name}`, run: () => p.onPin(x.id) })),
          ]}
        />
      ) : null}
      <Sheet
        open={!!archive}
        onClose={() => setArchive(null)}
        placement="center"
        title={`Archive ${archive?.name ?? ''}?`}
        subtitle="It leaves the Explorer. Its lanes, tasks and history stay readable."
        actions={
          <>
            <Button variant="ghost" onClick={() => setArchive(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (archive) p.onArchive(archive.id)
                setArchive(null)
              }}
            >
              Archive
            </Button>
          </>
        }
      >
        <p className="m-0 text-[14px] text-text-2">
          Running agents keep running. Unarchive it from Home › Projects.
        </p>
      </Sheet>
    </aside>
  )
}

const GUIDES = [0, 1, 2, 3]

function Node({
  row: r,
  first,
  prevKind,
  href,
  current,
  standalone,
  onToggle,
  onContext,
}: {
  row: ExplorerRow
  first: boolean
  prevKind: ExplorerRow['kind'] | undefined
  href: string
  current: boolean
  standalone: boolean
  onToggle: () => void
  onContext: (e: MouseEvent) => void
}) {
  const sep = r.kind === 'project' && !first && prevKind !== undefined
  const key = r.index !== undefined && r.index <= 9 ? tabShortcut(r.index, standalone) : undefined
  return (
    <li
      className={`xp-node is-${r.kind}${current ? ' is-current' : ''}${sep ? ' has-sep' : ''}`}
      data-node={r.id}
      style={{ '--d': r.depth } as React.CSSProperties}
    >
      {GUIDES.slice(0, r.depth).map((g) => (
        <span key={g} className="xp-guide" style={{ left: 6 + g * 16 + 7 }} aria-hidden="true" />
      ))}
      <span className="xp-cv">
        {r.expandable ? (
          <button
            type="button"
            tabIndex={-1}
            className={`xp-chev${r.open ? ' is-open' : ''}`}
            aria-label={`${r.open ? 'Collapse' : 'Expand'} ${r.label}`}
            aria-expanded={r.open}
            onClick={onToggle}
          >
            <ChevronRight size={14} strokeWidth={1.5} aria-hidden="true" />
          </button>
        ) : null}
      </span>
      <Link
        to={href}
        className="xp-link"
        data-tab={r.kind === 'home' ? 'home' : r.kind === 'project' ? r.projectId : undefined}
        aria-current={current ? 'page' : undefined}
        aria-label={r.label}
        {...(r.kind === 'project' || r.kind === 'home'
          ? {
              'aria-keyshortcuts': tabKeyShortcuts(r.index ?? 0, standalone),
              title: key ? `${r.label} (${key})` : r.label,
            }
          : { title: r.label })}
        onContextMenu={onContext}
        onDoubleClick={r.expandable ? onToggle : undefined}
      >
        <span className="xp-ic" aria-hidden="true">
          {r.kind === 'home' ? (
            <House size={16} />
          ) : r.kind === 'project' ? (
            <span className="xp-mono">{r.label.trim().charAt(0).toUpperCase()}</span>
          ) : r.status ? (
            <StatusIcon kind={r.status} live={r.status === 'run'} />
          ) : null}
        </span>
        <span className="xp-label">{r.label}</span>
      </Link>
      {r.counts && (r.kind === 'project' || r.kind === 'home') ? (
        <span className="xp-counts">
          <Count n={r.counts.blocked} kind="blocked" label={`${r.counts.blocked} blocked`} />
          <Count n={r.counts.needs} kind="wait" label={`${r.counts.needs} need you`} />
          <Count n={r.counts.running} kind="run" label={`${r.counts.running} running`} />
        </span>
      ) : r.counts?.needs ? (
        <span className="xp-counts is-child">
          <Count n={r.counts.needs} kind="wait" label={`${r.counts.needs} need you`} />
        </span>
      ) : r.meta ? (
        <span className="xp-meta">{r.meta}</span>
      ) : null}
    </li>
  )
}

function Count({ n, kind, label }: { n: number; kind: 'blocked' | 'wait' | 'run'; label: string }) {
  if (!n) return <span className="xp-count" />
  return (
    <span className={`xp-count is-${kind}`} title={label}>
      <span className="sr-only">{label}</span>
      <StatusIcon kind={kind} live={false} />
      <span aria-hidden="true">{n}</span>
    </span>
  )
}

/** One line per machine with its load; stale data gets an amber clock (drills to Settings). */
function Machines() {
  const machines = useMachines()
  const now = Date.now()
  const list = machines.data ?? []
  if (!list.length) return null
  return (
    <ul className="xp-machines" aria-label="Machines">
      {list.map((m) => {
        const l = machineLine(m, now)
        return (
          <li key={m.id}>
            <Link to="/settings/machines" className={`xp-machine is-${l.tone}`}>
              <span className="xp-machine__ic" aria-hidden="true">
                {l.tone === 'stale' ? (
                  <Clock3 size={16} />
                ) : (
                  <StatusIcon kind={l.tone === 'off' ? 'idle' : 'run'} live={false} />
                )}
              </span>
              {l.text}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

interface MenuItem {
  label: string
  run: () => void
  sep?: boolean
}

function XpMenu({
  at,
  items,
  onClose,
  label = 'Project actions',
}: {
  at: { x: number; y: number }
  items: MenuItem[]
  onClose: () => void
  label?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    ref.current?.querySelector<HTMLElement>('button')?.focus()
    const onDown = (e: globalThis.PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) close.current()
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close.current()
        prev?.focus()
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const all = [...(ref.current?.querySelectorAll<HTMLElement>('button') ?? [])]
        const i = all.indexOf(document.activeElement as HTMLElement)
        all[(i + (e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length]?.focus()
      }
    }
    addEventListener('pointerdown', onDown)
    addEventListener('keydown', onKey)
    return () => {
      removeEventListener('pointerdown', onDown)
      removeEventListener('keydown', onKey)
    }
  }, [])
  return (
    <div
      ref={ref}
      className="lk-menu xp-menu"
      role="menu"
      aria-label={label}
      style={{ left: Math.max(8, at.x), top: at.y }}
    >
      {items.map((m) => (
        <div key={m.label} className="contents">
          {m.sep ? <hr /> : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              m.run()
              close.current()
            }}
          >
            {m.label}
          </button>
        </div>
      ))}
    </div>
  )
}
